/**
 * Markdown to AST Parser
 * Converts markdown text back to AST structures (for LLM responses)
 */

import { Block, ContentAST, InlineContent, ListItem, Mark, TextDirection, TextNode } from './astTypes'
import {
  createDivider,
  createEmbed,
  createEmptyAST,
  createEncryptedNode,
  createHeading,
  createInlineEmbed,
  createList,
  createListItem,
  createParagraph,
  createQuote,
  createTextNode,
} from './astUtils'
import { isEmbedReference, isInlineEmbedReference } from './embedReference'
import { MARK_FORMATS } from './markFormats'
import { normalizeWritingHeadings, parseSectionOpening } from './writingSyntax'

/**
 * Parse the optional trailing attributes of an [[encrypted ...]] marker into the
 * consent fields. Unknown attributes are ignored so older or newer markers still parse.
 */
function parseEncryptedConsentAttrs(attrs: string | undefined): {
  character?: string
  authorHeld?: true
  released?: true
} {
  if (!attrs) {
    return {}
  }
  const out: { character?: string; authorHeld?: true; released?: true } = {}
  for (const match of attrs.matchAll(/\s+([a-zA-Z]+)="([^"]*)"/g)) {
    const [, name, value] = match
    if (name === 'character' && value) {
      out.character = value
    } else if (name === 'authorHeld' && value === '1') {
      out.authorHeld = true
    } else if (name === 'released' && value === '1') {
      out.released = true
    }
  }
  return out
}

/**
 * A list-item line: indent, marker, and content. The content group is optional
 * so a marker on its own line (`-`, `11.`) reads back as an *empty* list item —
 * that is how the renderer writes an empty bullet, and without this the line
 * falls through to the paragraph parser and turns into literal "-" text.
 * `---` / `***` still fail to match, so dividers stay dividers.
 */
const LIST_ITEM_LINE = /^(\s*)([*\-+]|\d+\.)(?:\s+(.*))?$/

export interface MarkdownParserOptions {
  preserveIds?: boolean // Whether to preserve block IDs from metadata
  defaultLang?: 'he' | 'en' | 'mixed'
  defaultDir?: TextDirection
}

export interface ParseResult {
  success: boolean
  ast?: ContentAST
  errors: ParseError[]
}

export interface ParseError {
  line?: number
  message: string
  code: string
}

/**
 * Parses Markdown to AST
 */
export class MarkdownParser {
  private options: Required<MarkdownParserOptions>
  private errors: ParseError[] = []
  private currentLine = 0

  constructor(options: MarkdownParserOptions = {}) {
    this.options = {
      preserveIds: false,
      defaultLang: 'mixed',
      defaultDir: 'ltr',
      ...options,
    }
  }

  /**
   * Parses markdown text to AST
   */
  public parse(markdown: string): ParseResult {
    this.errors = []
    this.currentLine = 0

    try {
      const lines = markdown.split('\n')
      const { metadata, contentLines } = this.extractFrontmatter(lines)

      // Join lines that span unclosed [[encrypted]] blocks — text nodes may contain \n
      // from Lexical LineBreakNodes, causing encrypted markers to span multiple lines.
      const joinedLines = this.joinMultiLineEncryptedBlocks(contentLines)

      // Create base AST with metadata
      const ast = this.createBaseAST(metadata)

      // Parse content blocks
      ast.blocks = normalizeWritingHeadings(this.parseBlocks(joinedLines))

      return {
        success: this.errors.length === 0,
        ast,
        errors: this.errors,
      }
    } catch (error) {
      this.addError(`Failed to parse markdown: ${error}`, 'parse_error')
      return {
        success: false,
        errors: this.errors,
      }
    }
  }

  /**
   * Joins lines that are inside unclosed [[encrypted ...]] ... [[/encrypted]] blocks.
   * Text nodes may contain \n (from Lexical LineBreakNodes), which causes the rendered
   * [[encrypted]] markers to span multiple lines. This collapses them back onto a single
   * line so block-level parsing works correctly, preserving the \n within the content.
   */
  private joinMultiLineEncryptedBlocks(lines: string[]): string[] {
    const result: string[] = []
    let i = 0
    const prose = (line: string): string => line.replace(/<!-- inline-embed:[\s\S]*? -->/g, '')

    while (i < lines.length) {
      const line = lines[i]
      // A reference filename is JSON, not manuscript markup.
      if (line.trimStart().startsWith('<!-- embed:')) {
        result.push(line)
        i++
        continue
      }
      // Count open vs close markers on this line
      const opens = (prose(line).match(/\[\[encrypted\s/g) || []).length
      const closes = (prose(line).match(/\[\[\/encrypted\]\]/g) || []).length

      if (opens > closes) {
        // Unclosed encrypted block — join subsequent lines until balanced
        const parts = [line]
        let depth = opens - closes
        i++
        while (i < lines.length && depth > 0) {
          const nextOpens = (prose(lines[i]).match(/\[\[encrypted\s/g) || []).length
          const nextCloses = (prose(lines[i]).match(/\[\[\/encrypted\]\]/g) || []).length
          depth += nextOpens - nextCloses
          parts.push(lines[i])
          i++
        }
        // Each joined-away newline was rendered as a markdown soft break — two
        // trailing spaces plus the line ending. The `\n` we join with carries the
        // break, so drop those spaces; keeping them would fold them into the
        // encrypted text and the next render would add two more on top.
        // The final part keeps its trailing spaces: they sit *after* the closing
        // marker and belong to the paragraph line, whose own join reads them.
        const inner = parts.slice(0, -1).map((part) => part.replace(/[ \t]+$/, ''))
        result.push([...inner, parts[parts.length - 1]].join('\n'))
      } else {
        result.push(line)
        i++
      }
    }

    return result
  }

  /**
   * Extracts frontmatter from markdown
   */
  private extractFrontmatter(lines: string[]): { metadata: any; contentLines: string[] } {
    if (lines.length === 0 || !lines[0].startsWith('---')) {
      return { metadata: {}, contentLines: lines }
    }

    let endIndex = -1
    for (let i = 1; i < lines.length; i++) {
      if (lines[i] === '---') {
        endIndex = i
        break
      }
    }

    if (endIndex === -1) {
      this.addError('Unterminated frontmatter block', 'frontmatter_error')
      return { metadata: {}, contentLines: lines }
    }

    const frontmatterLines = lines.slice(1, endIndex)
    const contentLines = lines.slice(endIndex + 1)

    const metadata = this.parseFrontmatter(frontmatterLines)
    return { metadata, contentLines }
  }

  /**
   * Parses frontmatter key-value pairs
   */
  private parseFrontmatter(lines: string[]): any {
    const metadata: any = {}

    for (const line of lines) {
      const match = line.match(/^(\w+):\s*(.+)$/)
      if (match) {
        const [, key, value] = match

        // Parse quoted strings
        if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
          metadata[key] = value.slice(1, -1).replace(/''/g, "'")
        } else {
          metadata[key] = value
        }
      }
    }

    return metadata
  }

  /**
   * Creates base AST structure from metadata
   */
  private createBaseAST(metadata: any): ContentAST {
    const lang = metadata.lang || this.options.defaultLang
    const dir = metadata.dir || this.options.defaultDir
    const title = metadata.title

    const ast = createEmptyAST(lang, dir)
    if (title) {
      ast.meta.title = title
    }

    // Clear default empty paragraph
    ast.blocks = []

    return ast
  }

  /**
   * Parses content blocks from markdown lines
   */
  private parseBlocks(lines: string[]): Block[] {
    const MAX_BLOCKS = 10_000
    const blocks: Block[] = []
    let i = 0

    while (i < lines.length) {
      const line = lines[i]
      this.currentLine = i + 1

      // Skip empty lines
      if (!line.trim()) {
        i++
        continue
      }

      // Empty paragraph marker
      if (line.trim() === '<!-- empty -->') {
        blocks.push(createParagraph())
        i++
        continue
      }

      // Parse different block types
      const parseResult = this.parseBlockLine(line, lines, i)
      if (parseResult.block) {
        blocks.push(parseResult.block)
      }

      // Safety: if parser didn't advance, skip the line to avoid infinite loop
      if (parseResult.nextIndex <= i) {
        this.addError(`Parser stuck at line ${i + 1}, skipping: ${line.slice(0, 80)}`, 'stuck_parser')
        i++
        continue
      }

      i = parseResult.nextIndex

      if (blocks.length >= MAX_BLOCKS) {
        this.addError(`Exceeded ${MAX_BLOCKS} blocks, truncating`, 'too_many_blocks')
        break
      }
    }

    return blocks
  }

  /**
   * Parses a single block from a line
   */
  private parseBlockLine(
    line: string,
    allLines: string[],
    startIndex: number,
  ): {
    block?: Block
    nextIndex: number
  } {
    const trimmed = line.trim()

    // Source embeds are standalone blocks. Reject damaged references explicitly
    // so a malformed marker cannot silently turn into prose on the next save.
    if (trimmed.startsWith('<!-- embed:')) {
      const match = trimmed.match(/^<!-- embed:(.+) -->$/)
      try {
        const source: unknown = match ? JSON.parse(match[1]) : undefined
        if (isEmbedReference(source)) {
          return { block: createEmbed(source), nextIndex: startIndex + 1 }
        }
      } catch {
        // Invalid JSON and invalid reference shapes produce the same parse error.
      }
      this.addError('Invalid embed reference', 'invalid_embed')
      return { nextIndex: startIndex + 1 }
    }

    // A standalone writing delimiter is its own block, including without blank lines.
    if (
      trimmed === '^^' ||
      trimmed === '^{' ||
      trimmed === '^[' ||
      trimmed === '}' ||
      trimmed === ']' ||
      parseSectionOpening(trimmed)
    ) {
      return { block: { ...createParagraph(), content: this.parseInlineContent(trimmed) }, nextIndex: startIndex + 1 }
    }

    // Heading
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      const [, hashes, content] = headingMatch
      const level = hashes.length as 1 | 2 | 3 | 4 | 5 | 6
      const inlineContent = this.parseInlineContent(content)

      return {
        block: {
          ...createHeading(level),
          content: inlineContent,
        },
        nextIndex: startIndex + 1,
      }
    }

    // Divider
    if (/^---+$|^\*\*\*+$|^___+$/.test(trimmed)) {
      return {
        block: createDivider(),
        nextIndex: startIndex + 1,
      }
    }

    // Quote
    if (trimmed.startsWith('> ')) {
      const { content, nextIndex } = this.parseQuoteBlock(allLines, startIndex)
      return {
        block: {
          ...createQuote(),
          content,
        },
        nextIndex,
      }
    }

    // List
    const listMatch = trimmed.match(LIST_ITEM_LINE)
    if (listMatch) {
      const { block, nextIndex } = this.parseListBlock(allLines, startIndex)
      return { block, nextIndex }
    }

    // Default to paragraph
    const { content, nextIndex } = this.parseParagraphBlock(allLines, startIndex)
    return {
      block: {
        ...createParagraph(),
        content,
      },
      nextIndex,
    }
  }

  /**
   * Parses a quote block (may span multiple lines)
   */
  private parseQuoteBlock(
    lines: string[],
    startIndex: number,
  ): {
    content: InlineContent[]
    nextIndex: number
  } {
    const quoteLines: string[] = []
    let i = startIndex

    while (i < lines.length) {
      const line = lines[i].trim()
      if (line.startsWith('> ')) {
        quoteLines.push(line.slice(2))
        i++
      } else if (line === '' && i + 1 < lines.length && lines[i + 1].trim().startsWith('> ')) {
        // Empty line within quote
        quoteLines.push('')
        i++
      } else {
        break
      }
    }

    const content = this.parseInlineContent(quoteLines.join(' '))
    return { content, nextIndex: i }
  }

  /**
   * Parses a paragraph block (may span multiple lines)
   */
  private parseParagraphBlock(
    lines: string[],
    startIndex: number,
  ): {
    content: InlineContent[]
    nextIndex: number
  } {
    const paragraphLines: string[] = []
    let i = startIndex

    while (i < lines.length) {
      const line = lines[i]
      const trimmed = line.trim()

      if (trimmed === '') {
        break
      }

      if (i > startIndex && (['^^', '^{', '^[', '}', ']'].includes(trimmed) || parseSectionOpening(trimmed))) {
        break
      }

      // Check if this looks like a different block type
      if (
        trimmed.match(/^#{1,6}\s/) || // heading
        trimmed.match(LIST_ITEM_LINE) || // list
        trimmed.startsWith('> ') || // quote
        trimmed.startsWith('<!-- embed:') || // source embed
        trimmed.match(/^---+$|^\*\*\*+$|^___+$/)
      ) {
        // divider
        if (i === startIndex) {
          // This line is a different block type
          break
        } else {
          // End of paragraph
          break
        }
      }

      paragraphLines.push(line)
      i++
    }

    // Join lines: if a line ends with 2+ trailing spaces (markdown soft break),
    // join with \n to preserve the line break. Otherwise join with space.
    let joined = ''
    for (let j = 0; j < paragraphLines.length; j++) {
      if (j > 0) {
        const prevRaw = paragraphLines[j - 1]
        const prevTrimmedEnd = prevRaw.trimEnd()
        const trailingSpaces = prevRaw.length - prevTrimmedEnd.length
        joined += trailingSpaces >= 2 ? '\n' : ' '
      }
      joined += paragraphLines[j].trim()
    }

    const content = this.parseInlineContent(joined)
    return { content, nextIndex: i }
  }

  /**
   * Parses a list block
   */
  private parseListBlock(
    lines: string[],
    startIndex: number,
  ): {
    block: Block
    nextIndex: number
  } {
    const firstMatch = lines[startIndex].match(LIST_ITEM_LINE)
    const baseIndent = firstMatch ? firstMatch[1].length : 0
    const isOrdered = firstMatch ? /^\d+\./.test(firstMatch[2]) : false

    const { items, nextIndex } = this.parseListItems(lines, startIndex, baseIndent)

    return {
      block: {
        ...createList(isOrdered),
        items,
      },
      nextIndex,
    }
  }

  /**
   * Parses list items recursively
   */
  private parseListItems(
    lines: string[],
    startIndex: number,
    baseIndent: number,
  ): {
    items: ListItem[]
    nextIndex: number
  } {
    const items: ListItem[] = []
    let i = startIndex

    while (i < lines.length) {
      const line = lines[i]
      const trimmed = line.trim()

      if (!trimmed) {
        // Blank line ends the current list — each blank-separated run of
        // list items becomes its own list block, matching the editor's AST.
        break
      }

      const match = line.match(LIST_ITEM_LINE)
      if (!match) {
        break
      }

      const [, indent, , content = ''] = match
      const currentIndent = indent.length

      if (currentIndent < baseIndent) {
        // Less indented, go back to parent level
        break
      }

      if (currentIndent === baseIndent) {
        // A soft line break inside an item (Shift+Enter) is written by the
        // renderer as two trailing spaces plus the continuation on the next
        // line. Those continuations belong to this item: without consuming
        // them the line falls out of the list as its own paragraph, the list
        // ends, and the following items open a *second* list block whose
        // numbering restarts at 1 (ListBlock has no `start`).
        let rawContent = content
        while (/ {2}$/.test(rawContent) && i + 1 < lines.length) {
          const continuation = lines[i + 1]
          if (
            !continuation.trim() ||
            LIST_ITEM_LINE.test(continuation) ||
            continuation.trimStart().startsWith('<!-- embed:')
          ) {
            break
          }
          // Strip the continuation's *leading* whitespace only: its own two
          // trailing spaces are the marker for the line after it, so trimming
          // both ends would stop a run of soft breaks after the first line.
          rawContent = rawContent.slice(0, -2) + '\n' + continuation.replace(/^\s+/, '')
          i++
        }

        // Same level, add item
        const inlineContent = this.parseInlineContent(rawContent)
        const item = {
          ...createListItem(),
          content: inlineContent,
        }

        // Check for nested items
        if (i + 1 < lines.length) {
          const nextLine = lines[i + 1]
          const nextMatch = nextLine.match(LIST_ITEM_LINE)
          if (nextMatch && nextMatch[1].length > currentIndent) {
            // Has nested items
            const { items: childItems, nextIndex: childNextIndex } = this.parseListItems(
              lines,
              i + 1,
              nextMatch[1].length,
            )
            item.children = childItems
            if (/^\d+\./.test(nextMatch[2])) {
              item.childrenOrdered = true
            }
            i = childNextIndex
          } else {
            i++
          }
        } else {
          i++
        }

        items.push(item)
      } else {
        // More indented but we're not parsing nested items here
        break
      }
    }

    return { items, nextIndex: i }
  }

  /**
   * Parses inline content (text with formatting and encryption)
   */
  private parseInlineContent(text: string): InlineContent[] {
    const nodes: InlineContent[] = []
    let remaining = text
    // let position = 0 // Used for future character-level tracking

    while (remaining.length > 0) {
      const encryptionMatch = remaining.match(
        /\[\[encrypted\s+id="([^"]+)"\s+algo="([^"]+)"\s+v="([^"]+)"((?:\s+[a-zA-Z]+="[^"]*")*)\]\]([\s\S]*?)\[\[\/encrypted\]\]/,
      )
      // Treat reference JSON as an atomic token before parsing any of its filename
      // as formatting or encryption syntax. Invalid markers must not save as prose.
      const embedIndex = remaining.indexOf('<!-- inline-embed:')
      if (embedIndex >= 0 && (!encryptionMatch || embedIndex < encryptionMatch.index!)) {
        nodes.push(...this.parseTextWithFormatting(remaining.slice(0, embedIndex)))
        const marker = remaining.slice(embedIndex).match(/^<!-- inline-embed:(.*?) -->/)
        try {
          const source: unknown = marker ? JSON.parse(marker[1]) : undefined
          if (!isInlineEmbedReference(source)) {
            throw new Error('Invalid inline embed reference')
          }
          nodes.push(createInlineEmbed(source))
        } catch {
          this.addError('Invalid inline embed reference', 'invalid_inline_embed')
        }
        remaining = marker ? remaining.slice(embedIndex + marker[0].length) : ''
        continue
      }
      if (encryptionMatch && encryptionMatch.index !== undefined) {
        const [fullMatch, id, , , extraAttrs, content] = encryptionMatch
        const consent = parseEncryptedConsentAttrs(extraAttrs)
        const matchIndex = encryptionMatch.index

        // Add text before encryption if any
        if (matchIndex > 0) {
          nodes.push(...this.parseTextWithFormatting(remaining.slice(0, matchIndex)))
        }

        // Add encryption node
        if (content === '[ENCRYPTED]') {
          // Reader mode - content is hidden
          nodes.push(createEncryptedNode([createTextNode('[ENCRYPTED]')], id, consent))
        } else {
          // Author mode - parse the content
          const encryptedContent = this.parseInlineContent(content)
          if (encryptedContent.some((node) => node.type === 'inline-embed')) {
            this.addError('Inline embeds cannot be stored inside encryption', 'invalid_inline_embed')
          }
          nodes.push(createEncryptedNode(encryptedContent as TextNode[], id, consent))
        }

        // Continue with remaining text
        remaining = remaining.slice(matchIndex + fullMatch.length)
        continue
      }

      // No encryption found, parse all remaining text with formatting
      nodes.push(...this.parseTextWithFormatting(remaining))
      break
    }

    return nodes
  }

  /**
   * Removes markdown escape backslashes (e.g. `\.` → `.`, `\!` → `!`)
   */
  private unescapeMarkdown(text: string): string {
    return text.replace(/\\([\\*_()#+\-.!|`>])/g, '$1')
  }

  /**
   * Parses text with markdown formatting (bold, italic, underline, links)
   */
  private parseTextWithFormatting(text: string): InlineContent[] {
    const nodes: InlineContent[] = []
    let remaining = text

    while (remaining.length > 0) {
      const match = this.findEarliestInlineMatch(remaining)

      if (match) {
        // Add text before the match
        if (match.index > 0) {
          nodes.push(createTextNode(this.unescapeMarkdown(remaining.slice(0, match.index))))
        }

        nodes.push(match.node)
        remaining = remaining.slice(match.index + match.length)
      } else {
        // No formatting found, emit as plain text
        if (remaining) {
          nodes.push(createTextNode(this.unescapeMarkdown(remaining)))
        }
        break
      }
    }

    return nodes
  }

  /**
   * Parses inner text and prepends a mark to all resulting text nodes.
   * Handles nested marks like **<u>text</u>** → TextNode with ['bold', 'underline'].
   */
  private parseWithAdditionalMark(innerText: string, mark: Mark): TextNode {
    const innerNodes = this.parseTextWithFormatting(innerText).filter((node): node is TextNode => node.type === 'text')
    if (innerNodes.length === 1) {
      const node = innerNodes[0]
      // node.text is already unescaped by parseTextWithFormatting
      const marks: Mark[] = [mark, ...(node.marks || [])]
      return createTextNode(node.text, marks)
    }
    // Fallback: treat as plain text with the mark
    return createTextNode(this.unescapeMarkdown(innerText), [mark])
  }

  /**
   * Finds the earliest inline formatting match in the text
   */
  private findEarliestInlineMatch(text: string): { index: number; length: number; node: InlineContent } | null {
    let best: { index: number; length: number; node: InlineContent } | null = null

    const tryMatch = (regex: RegExp, makeNode: (m: RegExpMatchArray) => InlineContent) => {
      const m = text.match(regex)
      if (m && m.index !== undefined && (!best || m.index < best.index)) {
        best = { index: m.index, length: m[0].length, node: makeNode(m) }
      }
    }

    // Bold+italic: ***text*** (must be checked before ** and *)
    tryMatch(/(?<!\\)\*\*\*([^*]+)\*\*\*/, (m) => {
      const innerNodes = this.parseTextWithFormatting(m[1]).filter((node): node is TextNode => node.type === 'text')
      if (innerNodes.length === 1) {
        const node = innerNodes[0]
        return createTextNode(node.text, ['bold', 'italic', ...(node.marks || [])])
      }
      return createTextNode(this.unescapeMarkdown(m[1]), ['bold', 'italic'])
    })

    // Markdown link: [text](url) — parse inner text for inline formatting
    tryMatch(/(?<![\\^])\[([^\]]+)\]\(([^)]+)\)/, (m) => {
      const innerContent = this.parseTextWithFormatting(m[1]).filter((node): node is TextNode => node.type === 'text')
      return {
        type: 'link' as const,
        // Normalize escaped URLs so we don't persist "escaped diff noise"
        // like \_ in query params across save cycles.
        url: this.unescapeMarkdown(m[2]),
        content: innerContent.length > 0 ? innerContent : [createTextNode(m[1])],
      }
    })

    // All mark types — driven by MARK_FORMATS table
    for (const entry of MARK_FORMATS) {
      const textGroup = entry.textGroup ?? 1
      tryMatch(entry.pattern, (m) => this.parseWithAdditionalMark(m[textGroup], entry.toMark(m)))
    }

    return best
  }

  /**
   * Adds a parse error
   */
  private addError(message: string, code: string): void {
    this.errors.push({
      line: this.currentLine,
      message,
      code,
    })
  }
}

/**
 * Convenience function to parse markdown to AST
 */
export const markdownToAst = (markdown: string, options?: MarkdownParserOptions): ParseResult => {
  const parser = new MarkdownParser(options)
  return parser.parse(markdown)
}

/**
 * Simple function that throws on parse errors
 */
export const parseMarkdown = (markdown: string, options?: MarkdownParserOptions): ContentAST => {
  const result = markdownToAst(markdown, options)
  if (!result.success || !result.ast) {
    const errorMessages = result.errors.map((e) => `Line ${e.line}: ${e.message}`).join('\n')
    throw new Error(`Failed to parse markdown:\n${errorMessages}`)
  }
  return result.ast
}
